import { useState } from "react";
import { AlertTriangle, CalendarClock, Save, Trash2 } from "lucide-react";
import type { CalendarException, OrganizationCalendar } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Notify, toast } from "@/components/ui/notify";
import { ShiftsEditor } from "@/features/calendar/ShiftsEditor";
import { fromDraft, toDraft } from "@/features/calendar/calendar-draft";
import type { ShiftForm } from "@/features/calendar/time-helpers";
import { AffectedLinesNotice } from "./AffectedLinesNotice";
import { useDeleteOrganizationCalendar, useUpdateOrganizationCalendar } from "./hooks";

interface Props {
  calendar: OrganizationCalendar | null;
  inheritingLineCount: number;
  canEdit: boolean;
}

/**
 * Semana sugerida no estado vazio: seg–sex, 08:00–18:00.
 *
 * Pré-preenchida e EDITÁVEL, não um placeholder. O gestor vê a FORMA do dado antes de precisar
 * entender o formulário — é o que sustenta a meta (cadastro completo em menos de cinco minutos sem
 * documentação). Um formulário vazio obriga a decifrar o que "turno semanal" quer dizer aqui antes
 * de conseguir digitar a primeira linha.
 */
const SEMANA_SUGERIDA: ShiftForm[] = [1, 2, 3, 4, 5].map((weekday) => ({
  weekday,
  startTime: "08:00",
  endTime: "18:00",
}));

// O expediente padrão da fábrica.
// Usa o MESMO `<ShiftsEditor>` da sheet da linha, não um parecido. Quem já cadastrou o
// expediente de uma linha não precisa reaprender nada aqui — e qualquer melhoria no editor chega
// aos dois lugares de uma vez.
export function OperatingCalendarSection({ calendar, inheritingLineCount, canEdit }: Props) {
  const seed = toDraft(calendar);
  // A condição é "ainda não há TURNOS", não "ainda não há calendário". Os dois pareciam a mesma
  // coisa até o gestor carregar os feriados primeiro: isso cria o calendário com `weeklyShifts`
  // vazio, e a semana sugerida sumia junto com o texto que a explicava — deixando um editor em
  // branco exatamente para quem ainda não sabe o que preencher. Carregar feriados antes da semana é
  // um caminho legítimo (a fábrica sabe quando NÃO opera antes de formalizar o turno).
  const semTurnos = (calendar?.weeklyShifts.length ?? 0) === 0;
  const [shifts, setShifts] = useState<ShiftForm[]>(
    semTurnos ? SEMANA_SUGERIDA.map((s) => ({ ...s })) : seed.shifts
  );
  const update = useUpdateOrganizationCalendar();
  const remove = useDeleteOrganizationCalendar();
  const busy = update.isPending || remove.isPending;

  // As exceções datadas e as regras recorrentes têm seções próprias; aqui só os turnos são
  // editados. Preservá-las no PUT é obrigatório — o endpoint SUBSTITUI o calendário inteiro, e
  // omiti-las apagaria os feriados ao salvar a semana.
  const exceptions: CalendarException[] = calendar?.exceptions ?? [];
  const recurringExceptions = calendar?.recurringExceptions ?? [];

  const { calendar: built, error } = fromDraft({ shifts, exceptions });

  async function save() {
    if (error) return;
    await update.mutateAsync({
      weeklyShifts: built?.weeklyShifts ?? [],
      exceptions,
      recurringExceptions,
    });
    toast.success("Expediente da fábrica salvo.");
  }

  async function confirmClear() {
    // Destrutivo e de alcance amplo ⇒ ConfirmDialog. As linhas que herdam voltam a derivar
    // o tempo planejado das produções, e isso muda o indicador delas na hora.
    const ok = await Notify.confirm({
      variant: "danger",
      title: "Remover o expediente da fábrica",
      description:
        inheritingLineCount === 0
          ? "Os turnos, feriados e regras da fábrica serão apagados. Nenhuma linha segue o padrão no momento."
          : `Os turnos, feriados e regras da fábrica serão apagados. ${inheritingLineCount} ${
              inheritingLineCount === 1 ? "linha volta" : "linhas voltam"
            } a calcular o tempo planejado a partir das ordens de produção.`,
      confirmLabel: "Remover",
    });
    if (!ok) return;
    await remove.mutateAsync();
    setShifts(SEMANA_SUGERIDA.map((s) => ({ ...s })));
    toast.success("Expediente da fábrica removido.");
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-text-tertiary" aria-hidden />
          Expediente padrão
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {semTurnos ? (
          <Notify.Empty
            title="A fábrica ainda não declarou expediente"
            description="Comece pela semana padrão abaixo — ela já vem preenchida com seg–sex, 08:00 às 18:00. Ajuste os horários e salve; as linhas que não têm expediente próprio passam a segui-la."
          />
        ) : null}

        <ShiftsEditor
          value={shifts}
          onChange={setShifts}
          disabled={!canEdit || busy}
          emptyHint="Sem turnos, a fábrica não declara expediente padrão e as linhas que a seguem derivam o tempo planejado das ordens."
        />

        {error ? (
          <p
            className="flex items-center gap-1.5 rounded-md bg-rd-danger-50 px-3 py-2 text-[12px] text-rd-danger-600"
            role="alert"
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden /> {error}
          </p>
        ) : null}

        {canEdit ? (
          <>
            <AffectedLinesNotice count={inheritingLineCount} />
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" disabled={busy || !!error} onClick={() => void save()}>
                <Save className="mr-1.5 h-4 w-4" /> Salvar expediente
              </Button>
              {calendar ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void confirmClear()}
                >
                  <Trash2 className="mr-1.5 h-4 w-4" /> Remover expediente
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
